/**
 * Links in der App öffnen, ohne die App zu verlassen. (24.09.2026,
 * Ticket 2396, ab Build 77)
 *
 * Martin: *„Statt die Datei innerhalb der nativen App zu öffnen, öffnet sich
 * direkt die Datei, und die native App tritt dabei komplett in den
 * Hintergrund/verschwindet.“*
 *
 * ## Warum die App verschwand (im Capacitor-Quelltext nachgelesen)
 *
 * `WebViewDelegationHandler.webView(_:createWebViewWith:…)` — der Weg jedes
 * `target="_blank"` — ruft `UIApplication.shared.open(url)`: Safari geht
 * auf, die App in den Hintergrund. Ein `<a download>` ohne Ziel navigiert
 * die WebView selbst zur Datei (voice.vespera.at ist erlaubte Adresse) —
 * danach steht die Datei im Fenster und die Oberfläche ist weg.
 *
 * ## Der Weg ab Build 77
 *
 * - Fremde Adressen öffnet `@capacitor/browser` (SFSafariViewController) —
 *   ein Blatt ÜBER der App, „Fertig" führt zurück.
 * - Dateien vom EIGENEN Server gehen in die Ansicht in der App
 *   (pdf-schau.ts, ein Rahmen mit Schließen-Knopf). ⚠️ Nicht in den
 *   SFSafariViewController: der teilt die Anmeldung der App nicht, und
 *   /uploads, /downloads und /api verlangen sie (401).
 * - Ein Link mit `data-app-browser="immer"` (der Ausweg „Im Browser
 *   öffnen" jener Ansicht) geht immer ins Blatt — sonst öffnete er sich
 *   selbst noch einmal.
 *
 * ## Nachprüfung Build 77 — was NICHT ins Blatt oder in die Ansicht gehört
 *
 * - Links, die an eine ANDERE App übergeben (WhatsApp `wa.me`, Telegram
 *   `t.me`, Karten, YouTube …): Bis Build 76 gingen sie über
 *   `UIApplication.shared.open` als Universal Link direkt in die App. Im
 *   SFSafariViewController löst ein direkt geladener Universal Link die App
 *   NICHT aus — man landete auf der wa.me-Webseite im Blatt statt im Chat.
 *   Sie bleiben darum „normal" (`uebergibtAnApp`), ebenso jeder Link mit
 *   `data-app-browser="nie"`.
 * - Eigene Adressen, die keine Datei sind (`/einstellungen`, ein
 *   Markdown-Link auf eine Route): In der Ansicht lüde eine zweite,
 *   vollständige Vespera im Rahmen — mit eigener Start-Logik, Herzschlag
 *   und Vorlesen. Nur Datei-Adressen (`istDateiAdresse`) kommen in die
 *   Ansicht; alles andere bleibt, wie es bis Build 76 war.
 * - Eigene Dateien, die die Ansicht nicht zeigen kann (.zip, .dmg …): eine
 *   leere Fläche. Auch sie bleiben „normal"; die zwei Installer-Knöpfe der
 *   Einstellungen zeigt die App gar nicht erst (settings-view.ts).
 *
 * ⚠️ Nur in der App, und nur, wenn die installierte Fassung das Plugin
 *    „Browser" mitbringt (ab Build 77). Im Browser und in älteren
 *    App-Fassungen bleibt jeder Link, wie er ist.
 * ⚠️ Gehört ein Klick schon einem eigenen Hörer (`preventDefault`, etwa der
 *    Projekt-Betrachter), bleibt er dort.
 * ⚠️ `window.open(…)`-Aufrufe fasst das NICHT an — manche erwarten ein
 *    Fenster zurück (Anmelde-Fenster, Druck). Das bleibt eine eigene Frage.
 */
import { getCapacitorPlugin, isNativePlatform, nativesPluginKennt } from "./native-voice.js";

interface BrowserPlugin {
  open(o: { url: string; presentationStyle?: "fullscreen" | "popover" }): Promise<void>;
}

/**
 * Hosts, deren Links an eine andere App übergeben (Universal Links). Ohne
 * `www.`. Google Maps steckt zusätzlich unter google.<land>/maps (unten).
 */
const APP_UEBERGABE = new Set([
  "wa.me",
  "api.whatsapp.com",
  "chat.whatsapp.com",
  "t.me",
  "telegram.me",
  "maps.google.com",
  "maps.app.goo.gl",
  "maps.apple.com",
  "youtube.com",
  "m.youtube.com",
  "youtu.be",
  "music.apple.com",
  "open.spotify.com",
]);

/** Übergibt dieser Link an eine andere App (WhatsApp, Telegram, Karten …)? */
export function uebergibtAnApp(ziel: URL): boolean {
  const host = ziel.hostname.toLowerCase().replace(/^www\./, "");
  if (APP_UEBERGABE.has(host)) return true;
  // google.com/maps, google.at/maps, google.co.uk/maps …
  return /^google\.[a-z.]+$/.test(host) && /^\/maps(\/|$)/.test(ziel.pathname);
}

/** Was die Ansicht in der App zeigen kann — nach der Endung. */
const ANSICHT_ENDUNGEN = new Set([
  "pdf", "png", "jpg", "jpeg", "gif", "webp", "heic", "svg",
  "mp3", "m4a", "aac", "wav", "ogg", "oga", "opus", "mp4", "m4v", "mov", "webm",
  "txt", "csv", "md",
]);

/**
 * Ist diese EIGENE Adresse eine Datei, die die Ansicht zeigen kann?
 * Mit Endung entscheidet die Endung (.zip nein, .pdf ja); ohne Endung nur
 * die Datei-Wege des Servers (/uploads, /downloads, /api/…/pdf).
 */
export function istDateiAdresse(ziel: URL): boolean {
  const pfad = ziel.pathname;
  const letzter = pfad.split("/").pop() ?? "";
  const punkt = letzter.lastIndexOf(".");
  if (punkt > 0) return ANSICHT_ENDUNGEN.has(letzter.slice(punkt + 1).toLowerCase());
  return /^\/(uploads|downloads)\//.test(pfad) || /^\/api\/.+\/pdf$/.test(pfad);
}

/**
 * Die frühere Adresse dieser Installation. (26.09.2026, Ticket 2675)
 *
 * Bis heute zeigten Rechnungs-PDFs auf https://v8.ssi.at/downloads/… (ein
 * Rückfall in tools/faktura.ts). Die Adresse leitet auf voice.vespera.at um —
 * aber die App hielt sie für FREMD, und nach der Umleitung fehlte die
 * Anmeldung: Martin sah {"error":"auth-required"} statt der Rechnung. Neue
 * Links tragen die heutige Adresse; diese hier holt die alten im Verlauf ein.
 */
const FRUEHERE_EIGENE_HOSTS = new Set(["v8.ssi.at"]);

/**
 * Eine Datei der früheren eigenen Adresse (/downloads/, /uploads/) → dieselbe
 * Datei über die heutige Herkunft. Alles andere bleibt, wie es ist.
 */
function aufHeutigeHerkunft(ziel: URL, herkunft: string): URL {
  if (!FRUEHERE_EIGENE_HOSTS.has(ziel.hostname.toLowerCase())) return ziel;
  if (!/^\/(downloads|uploads)\//.test(ziel.pathname)) return ziel;
  try {
    return new URL(ziel.pathname + ziel.search + ziel.hash, new URL(herkunft).origin);
  } catch {
    return ziel;
  }
}

/** Dasselbe für eine Adresse als Text (pdf-schau, Karten-Knöpfe). */
export function heutigeAdresse(roh: string, herkunft: string): string {
  try {
    const ziel = new URL(roh, herkunft);
    const neu = aufHeutigeHerkunft(ziel, herkunft);
    return neu === ziel ? roh : neu.href;
  } catch {
    return roh;
  }
}

/**
 * Der Dateiname am Ende einer Adresse, lesbar („Rechnung 12.pdf“ statt
 * „Rechnung%2012.pdf“). Leer, wenn die Adresse keinen trägt. Der Titel der
 * Ansicht und der Name beim Teilen (pdf-schau.ts) kommen beide von hier.
 */
export function dateiNameAus(ziel: URL): string {
  const letzter = ziel.pathname.split("/").pop() ?? "";
  try {
    return decodeURIComponent(letzter);
  } catch {
    return letzter; // kaputte %-Folge: der rohe Name tut es auch
  }
}

type LinkWeg =
  | { art: "blatt"; url: string }
  | { art: "hier"; url: string }
  | { art: "ansicht"; url: string; titel: string }
  | { art: "normal" };

/**
 * Ist das eine EIGENE Seite (genau diese Herkunft, keine Datei der Ansicht)? Absolute Adresse oder `/…`. (29.10.2026)
 * Nicht: `//host/…` (protokollrelativ, fremd), andere Hosts, auch nicht www- oder Panel-Aliase.
 */
export function istEigeneSeite(roh: string, herkunft: string = location.href): boolean {
  if (!roh || roh.startsWith("//")) return false;
  try {
    const ziel = new URL(roh, herkunft);
    if (ziel.protocol !== "https:" && ziel.protocol !== "http:") return false;
    return ziel.origin === new URL(herkunft).origin && !istDateiAdresse(ziel) && !pfadMehrdeutig(ziel);
  } catch {
    return false;
  }
}

/** Beginnt der Pfad mit `//` oder `/\` (nach der Zerlegung der Adresse sind beide `//…`)? Dann ist er als Ziel mehrdeutig. */
function pfadMehrdeutig(ziel: URL): boolean {
  return ziel.pathname.startsWith("//") || ziel.pathname.startsWith("/\\");
}

/** Bringt die installierte App den In-App-Browser mit? */
export function inAppBrowserDa(): boolean {
  return nativesPluginKennt("Browser", "open");
}

/**
 * Wohin ein Link-Klick in der App gehört. Reine Entscheidung — geöffnet wird
 * in `oeffneImApp`.
 */
function linkWeg(a: HTMLAnchorElement, herkunft: string): LinkWeg {
  const roh = a.getAttribute("href");
  if (!roh) return { art: "normal" };
  let ziel: URL;
  try {
    ziel = new URL(roh, herkunft);
  } catch {
    return { art: "normal" };
  }
  // mailto:, tel:, blob:, data:, javascript: — nicht unsere Sache.
  if (ziel.protocol !== "https:" && ziel.protocol !== "http:") return { art: "normal" };
  // Ticket 2675: alte eigene Datei-Links im Verlauf sind EIGENE Dateien.
  ziel = aufHeutigeHerkunft(ziel, herkunft);
  if (a.dataset.appBrowser === "nie") return { art: "normal" };
  if (a.dataset.appBrowser === "immer") return { art: "blatt", url: ziel.href };
  const neuesFenster = a.target === "_blank" || a.hasAttribute("download");
  if (!neuesFenster) return { art: "normal" };
  if (ziel.origin === new URL(herkunft).origin) {
    /*
     * Nachprüfung Build 77: nur Dateien in die Ansicht, keine App-Seiten.
     * 29.10.2026 (Martin, Link /neun im Chat): eine eigene Seite mit `_blank` darf NICHT „normal“ bleiben — die WebView
     * reicht sie dann ans System (Safari ohne Anmeldung, „Not found“). Sie lädt hier, im selben Fenster, mit der Sitzung.
     */
    if (!istDateiAdresse(ziel)) {
      // ⚠️ Kein Open-Redirect: `https://eigen//evil.com/x` und `https://eigen/\evil.com` haben die eigene Herkunft, aber einen Pfad,
      //    den ein Pfad-Ziel als „//evil.com“ läse. Solche Pfade bleiben „normal“ und gehen nie über `location.assign`.
      if (pfadMehrdeutig(ziel)) return { art: "normal" };
      return a.target === "_blank" && !a.hasAttribute("download")
        ? { art: "hier", url: ziel.href }
        : { art: "normal" };
    }
    // Ein leeres `download` („download" ohne Namen) zählt wie keins.
    const name = a.getAttribute("download")?.trim();
    const letzter = dateiNameAus(ziel);
    const titel = name !== undefined && name !== "" ? name : letzter !== "" ? letzter : "Datei";
    return { art: "ansicht", url: ziel.href, titel };
  }
  // Nachprüfung Build 77: WhatsApp, Telegram, Karten … öffnen ihre App.
  if (uebergibtAnApp(ziel)) return { art: "normal" };
  return { art: "blatt", url: ziel.href };
}

/**
 * Die Hosts, die die App IN ihrer WebView lädt: `server.allowNavigation` in
 * voice-v8-native/capacitor.config.ts (dazu `server.url`). (26.09.2026,
 * Nachbesserung zu pre.3352)
 *
 * ⚠️ Abgeglichen wie Capacitor selbst (CAPInstanceConfiguration.doesHost):
 *    gleich viele Teile, „*“ steht für GENAU einen. „*.google.com“ trifft
 *    docs.google.com, aber weder google.com noch a.b.google.com. Die frühere
 *    Liste in openExternalUrl (`(^|\.)google\.(com|de)$`, `vespera\.at$` …)
 *    hielt auch google.com, www.vespera.at oder www.googleapis.com für
 *    „bleibt in der App“ — die gingen damit auf den Weg, der ohne Geste
 *    still verworfen wird, obwohl sie ans System hätten gehen können.
 * ⚠️ Ändert sich allowNavigation in der nativen App, muss diese Liste mit.
 */
const WEBVIEW_HOSTS = [
  "voice.vespera.at",
  "accounts.google.com",
  "accounts.google.de",
  "oauth2.googleapis.com",
  "*.google.com",
  "*.googleusercontent.com",
  "*.gstatic.com",
];

/** Lädt die App-WebView diesen Host selbst (statt ihn ans System zu geben)? */
export function bleibtInDerWebView(host: string): boolean {
  const teile = host.toLowerCase().split(".");
  return WEBVIEW_HOSTS.some((muster) => {
    const m = muster.split(".");
    return m.length === teile.length && m.every((t, i) => t === "*" || t === teile[i]);
  });
}

/**
 * Eine Adresse nach außen öffnen, auch OHNE frische Geste. (2026-07-04, aus
 * app.ts hierher gezogen am 26.09.2026, Ticket 2679)
 *
 * In der nativen WKWebView ist `window.open` ohne Geste blockiert — still,
 * ohne Fehler. `location.assign` fängt Capacitor für jeden Host außerhalb
 * von `allowNavigation` ab und reicht ihn ans System (Safari, Universal
 * Links) — dafür braucht es keine Geste. Im Browser immer ein neuer Tab.
 *
 * ⚠️ Für die Hosts, die die WebView selbst lädt (`bleibtInDerWebView`),
 *    gibt es KEINEN Weg ohne Geste: `location.assign` ersetzte die App durch
 *    die Seite (kein Zurück), und `window.open` wird ohne Geste verworfen.
 *    Für sie bleibt `window.open` — es wirkt, solange die Geste noch gilt.
 *    Scheitert das Blatt, hilft darum der NÄCHSTE Tipp (siehe
 *    `blattKlemmt` in richteAppBrowserEin), für alle Hosts gleich.
 */
export function openExternalUrl(url: string): void {
  try {
    if (isNativePlatform() && !bleibtInDerWebView(new URL(url, location.href).hostname)) {
      window.location.assign(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    /* best-effort */
  }
}

/** Wie lange das Blatt antworten darf, bevor der Rückfall greift. */
export const BLATT_ZEITGRENZE_MS = 3000;

/**
 * Ist das Blatt in dieser Sitzung schon einmal gescheitert? Dann ist es
 * meist nicht nur einmal: Das Plugin hält ein früheres Blatt noch fest
 * („Unable to display URL“) oder antwortet gar nicht, bis die App neu
 * startet.
 */
let blattKlemmt = false;

/** Wohin eine Adresse zeigt — fürs Protokoll, ohne den Host selbst. */
function zielArt(url: string): "eigen" | "google" | "fremd" {
  let host: string;
  try {
    host = new URL(url, location.href).hostname.toLowerCase();
  } catch {
    return "fremd";
  }
  if (host === location.hostname.toLowerCase() || /(^|\.)vespera\.at$/.test(host)) return "eigen";
  if (/(^|\.)(google\.[a-z.]+|googleapis\.com|gstatic\.com|googleusercontent\.com)$/.test(host)) return "google";
  return "fremd";
}

/**
 * Der Grund als EINE saubere Zeile: keine Umbrüche oder Steuerzeichen (die
 * zerrissen das Protokoll), keine Adresse (die gehört nicht hinein), kurz.
 */
function grundAlsZeile(grund: unknown): string {
  const roh = grund instanceof Error ? grund.message : String(grund);
  return roh
    .replace(/[a-z][a-z0-9+.-]*:\/\/\S+/gi, "<adresse>")
    .replace(/[\p{Cc}\u2028\u2029]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
}

/** Eine Zeile ins Server-Protokoll: warum das Blatt nicht aufging. */
function meldeBlattFehler(url: string, grund: unknown): void {
  void fetch("/api/debug/ptt-timing", {
    method: "POST",
    body: JSON.stringify({ line: `[app-browser] blatt-fehler ziel=${zielArt(url)} grund=${grundAlsZeile(grund)}` }),
    headers: { "Content-Type": "text/plain" },
    keepalive: true,
    credentials: "include",
  }).catch(() => undefined);
}

/** `open` mit Zeitgrenze: Antwortet das Plugin nicht, gilt es als gescheitert. */
function mitZeitgrenze(p: Promise<void>, ms: number): Promise<void> {
  let uhr: ReturnType<typeof setTimeout> | undefined;
  const grenze = new Promise<never>((_, nein) => {
    uhr = setTimeout(() => nein(new Error(`keine Antwort nach ${ms / 1000} s`)), ms);
  });
  return Promise.race([p, grenze]).finally(() => clearTimeout(uhr));
}

/**
 * Öffnet, wie `linkWeg` entschieden hat.
 *
 * ⚠️ 26.09.2026 (Ticket 2679, Martin: Links „lassen sich nicht anklicken“):
 *    Der Klick ist hier schon `preventDefault`-t, und die Antwort des
 *    Browser-Plugins kommt als eigene Nachricht aus der App zurück — die
 *    Geste des Tipps ist dann vorbei. Der Rückfall `window.open` lief genau
 *    dort und wird von WKWebView ohne Geste still verworfen: Ging das Blatt
 *    nicht auf, tat der Tipp NICHTS.
 *    Jetzt für ALLE Hosts derselbe Rückfall, `openExternalUrl`, und eine
 *    Zeile ins Protokoll.
 * ⚠️ Nachbesserung: Das Plugin ruft den Abschluss nicht auf, solange schon
 *    ein Blatt offen ist — `open` hing dann für immer, und es gab weder
 *    Rückfall noch Zeile. Darum die Zeitgrenze (BLATT_ZEITGRENZE_MS).
 */
export async function oeffneImApp(weg: LinkWeg): Promise<void> {
  if (weg.art === "ansicht") {
    const { oeffnePdf } = await import("./pdf-schau.js");
    oeffnePdf(weg.url, weg.titel);
    return;
  }
  if (weg.art !== "blatt") return;
  const browser = getCapacitorPlugin<BrowserPlugin>("Browser");
  try {
    if (!browser) throw new Error("kein Browser-Plugin");
    await mitZeitgrenze(browser.open({ url: weg.url, presentationStyle: "popover" }), BLATT_ZEITGRENZE_MS);
  } catch (err) {
    blattKlemmt = true;
    meldeBlattFehler(weg.url, err);
    // Lieber raus als gar nicht — auf dem Weg, der auch ohne Geste geht.
    openExternalUrl(weg.url);
  }
}

/** Läuft die Seite als installierte App (Home-Bildschirm, ohne Browser-Leiste)? */
function alsAppGestartet(): boolean {
  try {
    return window.matchMedia?.("(display-mode: standalone)").matches === true || (navigator as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

let eingerichtet: (() => void) | null = null;

/**
 * Den Klick-Hörer einrichten — einmal, beim Start. In der Blase-Phase am
 * Dokument: eigene Hörer der Links laufen vorher und können übernehmen.
 * Gibt zurück, wie er wieder abgebaut wird.
 */
export function richteAppBrowserEin(): () => void {
  if (eingerichtet) return eingerichtet;
  const nativ = inAppBrowserDa();
  // Die Home-Bildschirm-App (PWA) hat kein Browser-Plugin, aber dasselbe Problem: `_blank` springt aus dem Fenster.
  const eigenstaendig = !nativ && alsAppGestartet();
  if (!nativ && !eigenstaendig) return () => undefined;
  const hoerer = (e: MouseEvent): void => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.("a[href]");
    if (!(a instanceof HTMLAnchorElement)) return;
    const weg = linkWeg(a, location.href);
    if (weg.art === "hier") {
      e.preventDefault();
      location.assign(weg.url);
      return;
    }
    if (!nativ) return;
    if (weg.art === "normal") return;
    /*
     * Nachbesserung zu pre.3352: Ist das Blatt schon gescheitert, bleibt der
     * Tipp bei der WebView. Die reicht einen Link mit target=_blank MIT der
     * Geste ans System (Capacitor: createWebViewWith → Safari) — für jeden
     * Host gleich, auch für die, die ohne Geste nicht aufgehen.
     */
    if (weg.art === "blatt" && blattKlemmt) return;
    e.preventDefault();
    void oeffneImApp(weg);
  };
  document.addEventListener("click", hoerer);
  eingerichtet = () => {
    document.removeEventListener("click", hoerer);
    eingerichtet = null;
    blattKlemmt = false;
  };
  return eingerichtet;
}
